import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { logger } from '@/lib/logger';
import type { RoleTemplate, StaffRoleAssignment, Permission } from '@/types/rbac';
import type { TeamRole } from '@/types/team';

let staffRoleAssignmentsRelationUnavailable = false;

function isMissingRelationError(error: unknown, relationName: string): boolean {
  if (!error || typeof error !== 'object') return false;
  const code = String((error as { code?: string }).code || '').toUpperCase();
  const message = String((error as { message?: string }).message || '').toLowerCase();
  return (
    code === '42P01' ||
    code === 'PGRST205' ||
    message.includes('does not exist') ||
    message.includes(`could not find the table '${relationName.toLowerCase()}'`) ||
    (message.includes('relation') && message.includes(relationName.toLowerCase()))
  );
}

/**
 * Get role templates (system roles + custom)
 */
export async function getRoleTemplates(): Promise<RoleTemplate[]> {
  if (!isSupabaseConfigured()) return getDefaultRoleTemplates();

  try {
    const { data } = await supabase
      .from('role_templates')
      .select('*')
      .or('tenant_type.eq.practice,tenant_type.eq.both')
      .order('sort_order', { ascending: true });

    if (data && data.length > 0) {
      return data.map(mapRoleTemplate);
    }
  } catch {
    // Table not available
  }

  return getDefaultRoleTemplates();
}

/**
 * Get staff role assignment for a user
 */
export async function getStaffRole(userId: string): Promise<StaffRoleAssignment | null> {
  if (!isSupabaseConfigured()) return null;
  if (staffRoleAssignmentsRelationUnavailable) return null;

  try {
    const { data, error } = await supabase
      .from('staff_role_assignments')
      .select('*')
      .eq('staff_id', userId)
      .eq('staff_type', 'practice')
      .maybeSingle();

    if (error) {
      if (isMissingRelationError(error, 'staff_role_assignments')) {
        staffRoleAssignmentsRelationUnavailable = true;
        return null;
      }
      return null;
    }

    if (data) return mapStaffRoleAssignment(data);
  } catch {
    // Table not available
  }

  return null;
}

/**
 * Check if user has a specific permission
 *
 * Priority chain (Strangler Fig):
 * 1. Supabase RPC check_permission (authoritative on allow)
 * 2. staff_role_assignments table → role template lookup
 * 3. practice_staff.role fallback → default role template permissions
 */
export async function hasPermission(userId: string, permission: Permission): Promise<boolean> {
  if (!isSupabaseConfigured()) return false; // Deny by default (Secure)

  try {
    const { data } = await supabase.rpc('check_permission', {
      p_staff_id: userId,
      p_staff_type: 'practice',
      p_permission: permission,
    });
    if (Boolean(data)) {
      return true;
    }
    // IMPORTANT: do not treat RPC=false as final deny.
    // In mixed deployments (legacy role rows / partial migrations),
    // RPC can return false while role-based fallbacks still grant permission.
    // Continue with fallback chain below.
  } catch {
    // RPC not available — use role-based fallback chain
  }

  // Fallback 1: staff_role_assignments table
  try {
    const assignment = await getStaffRole(userId);
    if (assignment?.roleTemplateId) {
      const templates = await getRoleTemplates();
      const roleTemplate = templates.find((t) => t.id === assignment.roleTemplateId);
      if (roleTemplate) {
        return checkPermissionInTemplate(roleTemplate, permission);
      }
    }
  } catch {
    // Table not available — continue to next fallback
  }

  // Fallback 2: practice_staff.role → default role template (critical path for web)
  const staffResult = await resolvePermissionFromStaffRole(userId, permission);
  if (staffResult) return true;

  // Fallback 3: practice owner check — user may own the practice without practice_staff row
  return resolvePermissionFromPracticeOwnership(userId);
}

/**
 * Resolve permission from practice_staff.role directly.
 * This is the primary fallback when staff_role_assignments is empty
 * (common in web deployments without full RBAC migration).
 */
async function resolvePermissionFromStaffRole(
  userId: string,
  permission: Permission,
): Promise<boolean> {
  try {
    const { data } = await supabase
      .from('practice_staff')
      .select('role')
      .eq('user_id', userId)
      .or('is_active.eq.true,is_active.is.null')
      .limit(1)
      .maybeSingle();

    if (!data?.role) return false;

    const staffRole = normalizeTeamRole(data.role);
    if (!staffRole) return false;
    const defaults = getDefaultRoleTemplates();
    const template = defaults.find((t) => t.id === staffRole);
    if (!template) return false;

    return checkPermissionInTemplate(template, permission);
  } catch (err) {
    logger.warn('[RBAC] practice_staff role fallback failed:', err);
    return false;
  }
}

/**
 * Final fallback: if the user directly owns a practice (practices.user_id),
 * grant all permissions — they are the practice owner.
 */
async function resolvePermissionFromPracticeOwnership(
  userId: string,
): Promise<boolean> {
  try {
    const { data } = await supabase
      .from('practices')
      .select('id')
      .eq('user_id', userId)
      .limit(1)
      .maybeSingle();

    if (data?.id) {
      logger.info('[RBAC] User is practice owner — granting permission');
      return true;
    }
  } catch (err) {
    logger.warn('[RBAC] practice ownership check failed:', err);
  }
  return false;
}

function normalizeTeamRole(value: unknown): TeamRole | null {
  if (typeof value !== 'string') return null;

  const normalized = value.trim().toLowerCase();

  const legacyRoleMap: Record<string, TeamRole> = {
    owner: 'owner',
    inhaber: 'owner',
    admin: 'admin',
    administrator: 'admin',
    doctor: 'doctor',
    arzt: 'doctor',
    mfa: 'mfa',
    reception: 'reception',
    rezeption: 'reception',
    labor: 'labor',
    lab: 'labor',
  };

  return legacyRoleMap[normalized] ?? null;
}

/**
 * Check if a permission is granted by a role template.
 * Handles dotted permissions (staff.invite) and wildcard patterns (staff.*, *).
 */
function checkPermissionInTemplate(
  template: RoleTemplate,
  permission: Permission,
): boolean {
  // Global wildcard
  if (template.permissions['*']) return true;

  // Direct match
  if (template.permissions[permission]) return true;

  // Dotted wildcard: "staff.invite" → check "staff.*"
  const dotCategory = permission.split('.')[0];
  if (dotCategory && template.permissions[`${dotCategory}.*`]) return true;

  return false;
}

// --- Defaults (when DB tables don't exist yet) ---

function getDefaultRoleTemplates(): RoleTemplate[] {
  return [
    {
      id: 'owner',
      nameDe: 'Inhaber',
      descriptionDe: 'Voller Zugriff auf alle Funktionen',
      tenantType: 'practice',
      permissions: { '*': true },
      isSystem: true,
      sortOrder: 0,
    },
    {
      id: 'admin',
      nameDe: 'Administrator',
      descriptionDe: 'Verwaltung aller Praxis-Funktionen',
      tenantType: 'practice',
      permissions: {
        'tasks.*': true,
        'appointments.*': true,
        'patients.*': true,
        'billing.*': true,
        'analytics.read': true,
        'staff.*': true,
        'settings.*': true,
        'team:manage': true,
      },
      isSystem: true,
      sortOrder: 1,
    },
    {
      id: 'doctor',
      nameDe: 'Arzt',
      descriptionDe: 'Klinische Funktionen und Patientenverwaltung',
      tenantType: 'practice',
      permissions: {
        'tasks.list': true,
        'tasks.read': true,
        'tasks.create': true,
        'tasks.update': true,
        'appointments.*': true,
        'patients.*': true,
        'billing.list': true,
        'billing.read': true,
        'billing.create': true,
        'staff.list': true,
        'staff.read': true,
      },
      isSystem: true,
      sortOrder: 2,
    },
    {
      id: 'mfa',
      nameDe: 'MFA',
      descriptionDe: 'Medizinische Fachangestellte',
      tenantType: 'practice',
      permissions: {
        'tasks.list': true,
        'tasks.read': true,
        'tasks.create': true,
        'tasks.update': true,
        'appointments.*': true,
        'patients.list': true,
        'patients.read': true,
        'patients.create': true,
        'patients.update': true,
        'billing.list': true,
        'billing.read': true,
        'staff.list': true,
        'staff.read': true,
      },
      isSystem: true,
      sortOrder: 3,
    },
    {
      id: 'reception',
      nameDe: 'Rezeption',
      descriptionDe: 'Empfang und Terminverwaltung',
      tenantType: 'practice',
      permissions: {
        'tasks.list': true,
        'tasks.read': true,
        'tasks.create': true,
        'tasks.update': true,
        'appointments.list': true,
        'appointments.read': true,
        'appointments.create': true,
        'appointments.update': true,
        'patients.list': true,
        'patients.read': true,
        'patients.create': true,
      },
      isSystem: true,
      sortOrder: 4,
    },
    {
      id: 'labor',
      nameDe: 'Labor',
      descriptionDe: 'Labormitarbeiter und Probenverarbeitung',
      tenantType: 'practice',
      permissions: {
        'tasks.list': true,
        'tasks.read': true,
        'tasks.create': true,
        'tasks.update': true,
        'patients.list': true,
        'patients.read': true,
        'billing.list': true,
        'billing.read': true,
      },
      isSystem: true,
      sortOrder: 5,
    },
  ];
}

// --- Mappers ---

function mapRoleTemplate(row: Record<string, unknown>): RoleTemplate {
  return {
    id: row.id as string,
    nameDe: row.name_de as string,
    descriptionDe: row.description_de as string | undefined,
    tenantType: row.tenant_type as RoleTemplate['tenantType'],
    permissions: (row.permissions as Record<string, boolean>) || {},
    isSystem: Boolean(row.is_system),
    sortOrder: (row.sort_order as number) || 0,
  };
}

function mapStaffRoleAssignment(row: Record<string, unknown>): StaffRoleAssignment {
  return {
    id: row.id as string,
    staffId: row.staff_id as string,
    staffType: row.staff_type as StaffRoleAssignment['staffType'],
    roleTemplateId: row.role_template_id as string | undefined,
    customRoleId: row.custom_role_id as string | undefined,
    permissionOverrides: (row.permission_overrides as Record<string, boolean>) || {},
    assignedAt: row.assigned_at as string,
  };
}
